/**
 * SidebarSection - Collapsible navigation section with header
 */
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

interface SidebarSectionProps {
  title: string
  icon: React.ComponentType<{ className?: string }>
  items: NavItem[]
  isCollapsed: boolean // sidebar collapsed state
  defaultExpanded?: boolean
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

export function SidebarSection({
  title,
  icon: SectionIcon,
  items,
  isCollapsed,
  defaultExpanded = false,
}: SidebarSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const location = useLocation()

  // Check if any item in this section is active
  const hasActiveItem = items.some((item) => {
    if (item.href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(item.href)
  })

  // When sidebar is collapsed, show section as icon-only
  if (isCollapsed) {
    return (
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            title={item.name}
            className={({ isActive }) =>
              `group flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`
            }
          >
            <item.icon className="h-6 w-6 flex-shrink-0" />
          </NavLink>
        ))}
      </div>
    )
  }

  return (
    <div>
      {/* Section header - clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-2 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${
          hasActiveItem
            ? 'text-white bg-gray-700/50'
            : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
        }`}
      >
        <div className="flex items-center">
          <SectionIcon className="h-4 w-4 mr-2 flex-shrink-0" />
          {title}
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${
            isExpanded ? '' : '-rotate-90'
          }`}
        />
      </button>

      {/* Section items */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mt-1 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `group flex items-center pl-6 pr-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`
              }
            >
              <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
              {item.name}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  )
}
